<template>
  <section v-if="leadership?.length" class="leadership pe-section">
    <h3>
      Leadership
      <SectionActions
        v-show="isPowerEdit"
        class="actions"
        section="leadership"
        :actions="['delete', 'add']"
      />
    </h3>
    <section class="list">
      <ul class="list-disc list-outside">
        <template v-for="(item, index) in leadership" :key="item.description">
          <li
            v-if="item.description"
            :data-index="index"
            :data-name="item?.description"
            @keydown.enter.stop.prevent="e => addItem(e, index)"
            @keydown.delete.stop="e => removeLeadership(e, index)"
            class="leadershipItem"
          >
            <span
              v-editable="isPowerEdit"
              :data-target-path="`resume.leadership.${index}.description`"
              v-text="item.description"
            ></span>
          </li>
        </template>
      </ul>
    </section>
  </section>
</template>

<script lang="ts">
import { defineComponent, PropType, toRefs, nextTick } from 'vue'
import { Resume } from '@/types/resume/v1'
import SectionActions from './SectionActions.vue'
import ItemActions from './ItemActions.vue'
import { useStructuredResumeStore } from '@/store/pinia/structuredResume'

export default defineComponent({
  name: 'Leadership',
  components: {
    SectionActions,
    ItemActions,
  },
  props: {
    leadership: {
      type: Array as PropType<Resume.Leadership[]>,
      required: false,
      default: () => [],
    },
  },
  setup(props) {
    const store = useStructuredResumeStore()
    const { leadership } = toRefs(props)
    const addItem = async (event, index) => {
      leadership.value.splice(index + 1, 0, {
        description: 'New item...',
      })
      await nextTick()
      const nextItem = event.currentTarget?.parentElement
        ?.querySelector(`li.leadershipItem[data-index="${index + 1}"]`)
        .querySelector('span')
      await nextTick()
      focusElement(nextItem)
    }

    const removeLeadership = async (event, index) => {
      if (event.currentTarget?.innerText.trim().length > 0) {
        return
      }
      event.preventDefault()
      const previousItem = event.currentTarget?.parentElement
        ?.querySelector(`li.leadershipItem[data-index="${index - 1}"]`)
        .querySelector('span')
      setCursor(previousItem)
      store.cleanLeadership()
    }

    const setCursor = element => {
      if (!element) return
      element.focus()
      const range = document.createRange()
      const selection = window.getSelection()
      range.setStart(element, element.childNodes.length)
      range.setEnd(element, element.childNodes.length)
      range.collapse(true)
      selection?.removeAllRanges()
      selection?.addRange(range)
    }

    const focusElement = element => {
      element.focus()
      const selection = window.getSelection()
      selection?.removeAllRanges()
      const range = document.createRange()
      range.selectNodeContents(element)
      selection?.addRange(range)
    }

    return {
      isPowerEdit: store.isPowerEdit,
      addItem,
      removeLeadership,
    }
  },
})
</script>

<style lang="postcss" scoped>
.leadership {
  @apply text-base-em leading-[inherit] pt-6-em @container;
  h3 {
    @apply leading-none text-xl-em flex items-center;
    .actions {
      @apply hidden;
    }
    &:hover {
      .actions {
        @apply flex;
      }
    }
  }
  .list {
    @apply pt-4-em;
    .leadershipItem {
      @apply text-sm-em relative min-w-[6px] h-auto ml-4-em leading-[inherit];
      .itemActions {
        @apply hidden;
      }
      &:hover {
        .itemActions {
          @apply flex;
        }
      }
    }
  }
}
</style>
